import React, { useEffect, useState } from 'react';
import { Col, Row, Space, Statistic, Input, Tooltip } from 'antd';
import { BookOutlined, UserOutlined, BarChartOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';

import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { useResponsive } from '../../hooks/useResponsive';
import { ClassCard as OldClassCard } from '../../components/classes/ClassCard/ClassCard';
import { ClassCard } from '../../components/common/cards';
import { DeleteConfirmModal, CreateClassModal, EditClassModal } from '../../components/common/modals';
import { CreateClassForm } from '../../components/forms/CreateClassForm/CreateClassForm';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { notificationController } from '../../controllers/notificationController';

// New layout components
import { 
  PageContainer, 
  ContentCard, 
  TwoColumnLayout, 
  FormSidebar, 
  StatsCard 
} from '../../components/layouts/PageLayouts';

// Business buttons
import { CreateButton } from '../../components/common/buttons';
import styled from 'styled-components';

// Modern UI components
const ModernSearchInput = styled(Input)`
  width: 280px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    border-color: #1677ff;
    box-shadow: 0 4px 12px rgba(22, 119, 255, 0.15);
  }
  
  &:focus, &.ant-input-focused {
    border-color: #1677ff;
    box-shadow: 0 4px 12px rgba(22, 119, 255, 0.15);
  }
  
  .ant-input {
    background: transparent;
    border: none;
    font-size: 14px;
    
    &::placeholder {
      color: rgba(0, 0, 0, 0.45);
    }
  }
`;

const CardsContainer = styled(Row)`
  @media (min-width: 769px) {
    min-height: 400px;
  }
`;

const HeaderActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 12px;
    
    ${ModernSearchInput} {
      width: 100%;
    }
  }
`;

const CompactStatsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  margin: 24px 0;
  padding: 20px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    margin: 16px 0;
    padding: 16px;
  }
`;

const CompactStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: white;
  }
  
  .stat-content {
    display: flex;
    flex-direction: column;
    
    .stat-value {
      font-size: 24px;
      font-weight: 700;
      color: #1a1a2e;
      line-height: 1;
    }
    
    .stat-label {
      font-size: 13px;
      color: rgba(0, 0, 0, 0.65);
      margin-top: 2px;
    }
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    
    .stat-content {
      text-align: center;
    }
  }
`;

const TitleStatsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  
  .badge-icon {
    width: 24px;
    height: 24px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: white;
  }
  
  .badge-content {
    display: flex;
    align-items: center;
    gap: 5px;
    
    .badge-value {
      font-size: 16px;
      font-weight: 600;
      color: #1a1a2e;
    }
    
    .badge-label {
      font-size: 13px;
      color: rgba(0, 0, 0, 0.65);
    }
  }
`;

const TitleStatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex: 1;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
    justify-content: flex-start;
  }
`;

const ClassesPage: React.FC = (props: any) => {
    const { isDesktop } = useResponsive();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getClassesRequest, deleteClassRequest, createClassRequest, updateClassRequest } = useActions();
    const { classes, delete: deleteState, create: createState, update: updateState } = useTypedSelector<any>(state => state.classes);
    const { isLoggedIn } = useTypedSelector<any>(state => state.user);
    const [userID, setUserID] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const { checkLoggedIn } = useActions();
    
    // Delete modal state
    const [deleteModal, setDeleteModal] = useState({
      isOpen: false,
      classId: null as number | null,
      className: ''
    });
    
    // Create modal state
    const [createModal, setCreateModal] = useState({
      isOpen: false
    });
    
    // Edit modal state
    const [editModal, setEditModal] = useState<{
      isOpen: boolean;
      classData: { id: number; name: string; faculty?: string } | null;
    }>({
      isOpen: false,
      classData: null
    });

  useEffect(() => {
    checkLoggedIn();
  }, []);
    
    // Handle userId initialization and classes loading
    useEffect(() => {
      // После авторизации через Keycloak userId уже в localStorage
      const storedUserId = localStorage.getItem("UserId");
      
      // Загружаем классы только если пользователь авторизован и userId есть
      if (isLoggedIn && storedUserId && storedUserId !== 'null') {
        // Add userId to URL if not present
        if (!searchParams.get('userId')) {
          setSearchParams({ userId: storedUserId });
        }
        
        getClassesRequest();
      }
    }, [isLoggedIn])
    
    // Handle successful deletion
    useEffect(() => {
      if (deleteState.success) {
        setDeleteModal({ isOpen: false, classId: null, className: '' });
        notificationController.success({ 
          message: 'Class deleted successfully',
          description: `"${deleteModal.className}" has been removed from your classes.`
        });
        // Refresh classes list
        getClassesRequest();
      }
    }, [deleteState.success]);
    
    // Handle delete error
    useEffect(() => {
      if (deleteState.error) {
        notificationController.error({ 
          message: 'Failed to delete class',
          description: deleteState.error
        });
      }
    }, [deleteState.error]);
    
    // Handle successful creation
    useEffect(() => {
      if (createState.success) {
        setCreateModal({ isOpen: false });
        notificationController.success({ 
          message: 'Class created successfully',
          description: 'Your new class has been added to the list.'
        });
        // Refresh classes list
        getClassesRequest();
      }
    }, [createState.success]);
    
    // Handle create error
    useEffect(() => {
      if (createState.error) {
        notificationController.error({ 
          message: 'Failed to create class',
          description: createState.error
        });
      }
    }, [createState.error]);
    
    // Handle successful update
    useEffect(() => {
      if (updateState?.success) {
        setEditModal({ isOpen: false, classData: null });
        notificationController.success({ 
          message: 'Class updated successfully',
          description: 'Your class has been updated.'
        });
        getClassesRequest();
      }
    }, [updateState?.success]);
    
    // Handle update error
    useEffect(() => {
      if (updateState?.error) {
        notificationController.error({ 
          message: 'Failed to update class',
          description: updateState.error
        });
      }
    }, [updateState?.error]);
    
    // Delete handlers
    const handleDeleteClick = (classId: number, className: string) => {
      setDeleteModal({
        isOpen: true,
        classId,
        className
      });
    };
    
    const handleDeleteConfirm = () => {
      if (deleteModal.classId) {
        deleteClassRequest(deleteModal.classId);
      }
    };
    
    const handleDeleteCancel = () => {
      setDeleteModal({ isOpen: false, classId: null, className: '' });
    };
    
    // Create handlers
    const handleCreateClick = () => {
      setCreateModal({ isOpen: true });
    };
    
    const handleCreateSubmit = (data: { name: string; year: string }) => {
      // Create class model according to existing API
      const classModel = {
        name: data.name,
        faculty: data.year // Using year as faculty field
      };
      
      createClassRequest(classModel);
    };
    
    const handleCreateCancel = () => {
      setCreateModal({ isOpen: false });
    };
    
    // Edit handlers
    const handleEditClick = (classId: number, className: string, faculty?: string) => {
      setEditModal({
        isOpen: true,
        classData: { id: classId, name: className, faculty }
      });
    };
    
    const handleEditSubmit = (data: { id: number; name: string; year: string }) => {
      const updateModel = {
        name: data.name,
        faculty: data.year
      };
      updateClassRequest(data.id, updateModel);
    };
    
    const handleEditCancel = () => {
      setEditModal({ isOpen: false, classData: null });
    };
  
    // Filter classes based on search query
    const filteredClasses = classes.filter((cls: any) => 
      cls.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      cls.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Calculate stats from classes
    const totalClasses = classes.length;
    const totalStudents = classes.reduce((sum: number, cls: any) => sum + (cls.countOfStudents || 0), 0);
    const totalExams = classes.reduce((sum: number, cls: any) => sum + (cls.exams_count || 0), 0);
    const averageGrade = classes.length > 0 
      ? Math.round(classes.reduce((sum: number, cls: any) => sum + (cls.averageMark || 0), 0) / classes.length)
      : 0;

    const compactStatsData = [
      {
        label: 'Total Classes',
        value: totalClasses,
        icon: <BookOutlined />,
        color: '#1677ff'
      },
      {
        label: 'Students',
        value: totalStudents,
        icon: <UserOutlined />,
        color: '#52c41a'
      },
      {
        label: 'Avg Grade',
        value: averageGrade || 'N/A',
        icon: <BarChartOutlined />,
        color: '#fa8c16'
      }
    ];

    const renderCompactStats = () => (
      <CompactStatsContainer>
        {compactStatsData.map((stat, index) => (
          <CompactStatItem key={index}>
            <div className="stat-icon" style={{ backgroundColor: stat.color }}>
              {stat.icon}
            </div>
            <div className="stat-content">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          </CompactStatItem>
        ))}
      </CompactStatsContainer>
    );

    const renderTitleStats = () => (
      <TitleStatsContainer>
        {compactStatsData.map((stat, index) => (
          <TitleStatsBadge key={index}>
            <div className="badge-icon" style={{ backgroundColor: stat.color }}>
              {stat.icon}
            </div>
            <div className="badge-content">
              <span className="badge-value">{stat.value}</span>
              <span className="badge-label">{stat.label}</span>
            </div>
          </TitleStatsBadge>
        ))}
      </TitleStatsContainer>
    );

    const pageActions = (
      <HeaderActionsContainer>
        <ModernSearchInput
          placeholder="Search classes..."
          prefix={<SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
        />
        <Tooltip 
          title="Create a new class and start assigning students"
          placement="bottom"
        >
          <CreateButton onClick={handleCreateClick}>
            New Class
          </CreateButton>
        </Tooltip>
      </HeaderActionsContainer>
    );

    const fullWidthContent = (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <ContentCard>
          <CardsContainer gutter={[24, 24]}>
            {filteredClasses.map((el: any) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={el.id}>
                <ClassCard 
                  classId={el.id}
                  className={el.name}
                  studentsCount={el.countOfStudents}
                  averageGrade={el.averageMark || 0}
                  onEditClass={() => handleEditClick(el.id, el.name, el.faculty)}
                  onDeleteClass={() => handleDeleteClick(el.id, el.name)}
                />
              </Col>
            ))}
            {filteredClasses.length === 0 && classes.length > 0 && (
              <Col span={24}>
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px',
                  color: 'rgba(0, 0, 0, 0.45)'
                }}>
                  <SearchOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <p style={{ fontSize: '16px', margin: 0 }}>
                    No classes found for "{searchQuery}"
                  </p>
                  <p style={{ fontSize: '14px', margin: '8px 0 0 0' }}>
                    Try adjusting your search terms
                  </p>
                </div>
              </Col>
            )}
            {classes.length === 0 && (
              <Col span={24}>
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px',
                  color: 'rgba(0, 0, 0, 0.45)'
                }}>
                  <BookOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <p style={{ fontSize: '18px', margin: '0 0 24px 0', fontWeight: 500, color: '#1a1a1a' }}>
                    You have no classes yet
                  </p>
                  <Tooltip title="Create a new class and start assigning students">
                    <CreateButton onClick={handleCreateClick}>
                      Create your first class
                    </CreateButton>
                  </Tooltip>
                </div>
              </Col>
            )}
          </CardsContainer>
        </ContentCard>
      </Space>
    );
  
    return (
      <>
        <PageTitle title="My Classes" description="Manage and organize your classes" url={window.location.href} noIndex={true}></PageTitle>
        <PageContainer
          title="My Classes"
          subtitle="Manage and organize your classes"
          actions={pageActions}
          titleStats={renderTitleStats()}
        >
          {fullWidthContent}
        </PageContainer>
        
        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={deleteModal.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Class"
          description="Are you sure you want to delete this class?"
          itemName={deleteModal.className}
          isLoading={deleteState.loading}
        />
        
        {/* Create Class Modal */}
        <CreateClassModal
          isOpen={createModal.isOpen}
          onClose={handleCreateCancel}
          onSubmit={handleCreateSubmit}
          isLoading={createState.loading}
        />
        
        {/* Edit Class Modal */}
        <EditClassModal
          isOpen={editModal.isOpen}
          onClose={handleEditCancel}
          onSubmit={handleEditSubmit}
          isLoading={updateState?.loading}
          classData={editModal.classData}
        />
      </>
    );
  };

export default ClassesPage